import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Utensils, Car, Loader2, CheckCircle, AlertCircle, Clock, XCircle } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Radisson Restaurant Parking Benefit Page (R9.110)
 * 
 * Purpose: QR code landing page for restaurant customers to apply their
 * complimentary parking benefit using credential-based authentication.
 * 
 * URL Contract:
 * /radisson/restaurant?credential=<opaque credential>
 * 
 * Flow:
 * 1. Customer scans restaurant QR code containing credential
 * 2. Page extracts credential from URL query parameter
 * 3. Customer enters their vehicle registration
 * 4. Submit → POST /api/radisson/benefit with {vehicle_registration, credential_id}
 * 5. Display backend response (success or error)
 * 
 * SECURITY (R9.110):
 * - credential_id from URL, NOT user-chosen
 * - Customer cannot choose benefit type (backend derives from credential)
 * - No benefit_type in POST payload
 * - No site_id submitted
 * - No session_id submitted
 * - No barrier/exit-auth/Stripe calls
 * - No credential storage (localStorage/sessionStorage/cookies)
 * - No credential console logging
 * - Credential not displayed in UI
 */
export default function RadissonRestaurant() {
  const [searchParams] = useSearchParams();
  const credential = searchParams.get("credential") || "";
  
  const [vrm, setVrm] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Check for valid credential from URL
  const hasValidCredential = credential && credential.trim().length > 0;

  // Normalize VRM: uppercase, remove spaces
  const normalizeVrm = (input) => {
    return input.toUpperCase().replace(/\s+/g, "").replace(/-/g, "");
  };

  const handleVrmChange = (e) => {
    const raw = e.target.value;
    setVrm(raw.toUpperCase());
    if (result || error) {
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block submission if no credential
    if (!hasValidCredential) {
      setError("Invalid QR code. Please scan a valid restaurant benefit QR code.");
      return;
    }
    
    const normalizedVrm = normalizeVrm(vrm);
    
    if (!normalizedVrm || normalizedVrm.length < 2) {
      setError("Please enter your vehicle registration");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // R9.110: POST exactly {vehicle_registration, credential_id}
      // No benefit_type, no site_id, no session_id
      const response = await axios.post(`${API}/radisson/benefit`, {
        vehicle_registration: normalizedVrm,
        credential_id: credential
      });

      setResult({
        success: true,
        message: response.data.message || "Your 3 hours free parking benefit has been applied.",
        isDuplicate: response.data.is_duplicate || false,
        freeMinutes: response.data.free_minutes || 180,
        vrm: normalizedVrm
      });
    } catch (err) {
      // Safe error handling - do not expose credential
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;

        if (status === 400) {
          setError(detail || "Unable to process your request. Please check your vehicle registration and try again.");
        } else if (status === 403) {
          setError("This QR code is not valid. Please contact reception.");
        } else if (status === 404) {
          setError(detail || "No active parking session found for this vehicle. Please ensure your vehicle has entered the car park.");
        } else if (status === 422) {
          setError("Invalid request. Please scan a valid QR code.");
        } else if (status === 503) {
          setError("Service temporarily unavailable. Please try again in a moment.");
        } else {
          setError("Unable to apply your benefit at this time. Please try again or contact reception.");
        }
      } else {
        setError("Unable to connect. Please check your internet connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVrm("");
    setResult(null);
    setError(null);
  };

  // Invalid QR State - missing credential
  if (!hasValidCredential) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col">
        <header className="bg-slate-900/80 border-b border-slate-700 px-4 py-4">
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h1 className="text-white font-semibold text-lg">Restaurant Parking Benefit</h1>
                <p className="text-slate-400 text-sm">Radisson Blu Hotel, London Canary Wharf East</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-6">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-red-400 font-semibold text-lg mb-2">Invalid QR Code</h2>
              <p className="text-slate-300 text-sm">
                This link is not valid. Please scan a restaurant benefit QR code or contact reception for assistance.
              </p>
            </div>
          </div>
        </main>

        <footer className="px-4 py-4 border-t border-slate-700">
          <div className="max-w-md mx-auto text-center">
            <p className="text-slate-500 text-xs">ParkWise Parking Management</p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-700 px-4 py-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Utensils className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-lg">Restaurant Parking Benefit</h1>
              <p className="text-slate-400 text-sm">Radisson Blu Hotel, London Canary Wharf East</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 py-6">
        <div className="max-w-md mx-auto">
          
          {/* Benefit Info Card */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <span className="text-white font-medium">3 Hours Free Parking</span>
            </div>
            <p className="text-slate-400 text-sm">
              Enter your vehicle registration to apply your restaurant parking benefit.
            </p>
          </div>

          {/* Success State */}
          {result?.success && (
            <div className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-emerald-400 font-semibold text-lg">
                    {result.isDuplicate ? "Benefit Already Applied" : "Benefit Applied"}
                  </h2>
                  <p className="text-slate-300 text-sm">{result.vrm}</p>
                </div>
              </div>
              <p className="text-slate-300 mb-4">{result.message}</p>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-amber-400 font-medium text-center">
                  {result.freeMinutes} minutes free parking
                </p>
              </div>
              <button
                onClick={handleReset}
                className="w-full mt-4 py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Enter Another Vehicle
              </button>
            </div>
          )}

          {/* Error State */}
          {error && !result && (
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* VRM Entry Form */}
          {!result?.success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="vrm" className="block text-slate-300 text-sm font-medium mb-2">
                  Vehicle Registration
                </label>
                <div className="relative">
                  <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    id="vrm"
                    type="text"
                    value={vrm}
                    onChange={handleVrmChange}
                    placeholder="e.g. AB12 CDE"
                    disabled={loading}
                    autoComplete="off"
                    autoCapitalize="characters"
                    className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-lg font-mono placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="vrm-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !vrm.trim()}
                className="w-full py-4 px-6 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="submit-benefit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Applying Benefit...
                  </>
                ) : (
                  "Apply Restaurant Benefit"
                )}
              </button>
            </form>
          )}

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">
              Need help? Please speak to a member of staff.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-4 border-t border-slate-700">
        <div className="max-w-md mx-auto text-center">
          <p className="text-slate-500 text-xs">
            ParkWise Parking Management
          </p>
        </div>
      </footer>
    </div>
  );
}
